import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import InfoTip from '../../components/novarium/InfoTip';
import { WaitRankingRowDTO } from '../../dto/ServiceStatisticsDTO';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import { formatInt, formatMinutes, formatPercent } from './statisticsUtils';
import WaitCauseList from './WaitCauseList';
import { LEVEL_LABEL_ID, LEVEL_TAG_CLASS, RANKING_LEVEL } from './waitLevelText';

/** Aviso general del período: parte de los pacientes esperó más de una hora. No es de un servicio, así que no tiene causa. */
export interface Wait60Notice {
    ratio: number;
    count: number;
    total: number;
}

interface AlertsDialogProps {
    visible: boolean;
    onHide: () => void;
    /** Los servicios y agendas con espera fuera de lo normal (las de «Dónde hay más espera», todas), de mayor a menor espera. */
    rows: WaitRankingRowDTO[];
    /** Aviso de más de una hora de espera; null si no llega al mínimo. */
    wait60: Wait60Notice | null;
    /** Servicio aplicado como filtro; 0 si no hay. Sus avisos no ofrecen "Ver servicio", porque ya se está viendo. */
    selectedServiceId: number;
    /** "Ver servicio": aplica el servicio como filtro de la pantalla. */
    onViewService: (serviceId: number) => void;
    /** En «Mi desempeño» el aviso general habla de las atenciones del profesional. */
    mode?: StatisticsMode;
}

/**
 * La lista completa de avisos del período (`Ver los N avisos` de «Dónde hay más espera»), en un Dialog de decisión de
 * Novarium con su único cierre. Cada aviso es una fila: nivel (con texto, no solo color), servicio con su espera media y
 * sus posibles causas (las mismas que el ranking, que calcula el backend) y, si no es el servicio elegido, "Ver servicio"
 * para filtrar la pantalla por él. Al final, el aviso general de espera de más de una hora, que no es de un servicio:
 * solo el dato, con un ícono neutro y sin nivel.
 */
export default function AlertsDialog({
    visible, onHide, rows, wait60, selectedServiceId, onViewService, mode = 'management',
}: AlertsDialogProps) {
    const intl = useIntl();

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            dismissableMask
            draggable={false}
            className="nv-scope nv-dialog ss-alerts-dialog"
            header={<span className="ss-alerts-head">
                {intl.formatMessage({ id: 'ServicesStatisticsAlertsTitle' })}
                <InfoTip text={intl.formatMessage({ id: 'ServicesStatisticsCausesNote' })} />
            </span>}
            footer={<Button type="button" className="buttonMain" label={intl.formatMessage({ id: 'ServicesStatisticsAlertsClose' })} onClick={onHide} />}
        >
            <div className="nv-dialog-body">
                <ul className="ss-alerts">
                    {rows.map(row => {
                        const level = RANKING_LEVEL[row.level];
                        return (
                            <li key={row.serviceId} className="ss-alert">
                                <Tag className={LEVEL_TAG_CLASS[level]} value={intl.formatMessage({ id: LEVEL_LABEL_ID[level] })} />
                                <div className="ss-alert__body">
                                    <span className="ss-alert__title">
                                        {row.serviceName}
                                        <span className="nv-muted"> · {formatMinutes(row.avgWaitMinutes)}</span>
                                    </span>
                                    <WaitCauseList causes={row.causes} level={row.level} />
                                </div>
                                {row.serviceId !== selectedServiceId && (
                                    <Button type="button" className="buttonMain3"
                                        label={intl.formatMessage({ id: modeMessageId(mode, 'ViewService') })}
                                        aria-label={intl.formatMessage({ id: 'ServicesStatisticsViewOf' },
                                            { action: intl.formatMessage({ id: modeMessageId(mode, 'ViewService') }), name: row.serviceName })}
                                        onClick={() => onViewService(row.serviceId)} />
                                )}
                            </li>
                        );
                    })}
                    {wait60 && (
                        <li className="ss-alert">
                            <i className="pi pi-info-circle ss-alert__icon" aria-hidden="true" />
                            <div className="ss-alert__body">
                                <span className="ss-alert__title">
                                    {intl.formatMessage({ id: modeMessageId(mode, 'AlertWait60') }, { ratio: formatPercent(wait60.ratio) })}
                                    <span className="nv-muted">
                                        {' · '}
                                        {intl.formatMessage({ id: 'ServicesStatisticsAlertOf' },
                                            { count: formatInt(wait60.count), total: formatInt(wait60.total) })}
                                    </span>
                                </span>
                            </div>
                        </li>
                    )}
                </ul>
            </div>
        </Dialog>
    );
}
